export interface EnablingClauseMatch {
  fullMatch: string;
  topic: string;
  delegateRawText: string;
  delegateType: DelegateRegulationType;
  startIndex: number;
  endIndex: number;
}

export type DelegateRegulationType = 'peraturan_pemerintah' | 'peraturan_menteri' | 'unknown';

export const DELEGATE_TYPE_MAP: Record<string, DelegateRegulationType> = {
  'Peraturan Pemerintah': 'peraturan_pemerintah',
  'Peraturan Menteri': 'peraturan_menteri',
};

export const REGULATION_TYPE_TO_DELEGATE: Record<string, string[]> = {
  peraturan_pemerintah: ['peraturan pemerintah'],
  peraturan_menteri: ['peraturan menteri'],
};

const ENABLING_CLAUSE_PATTERN =
  /Ketentuan\s+(?:lebih\s+lanjut\s+)?(?:mengenai|menyangkut|tentang)\s+([^,.]+?)\s+diatur\s+(?:dengan|oleh)\s+(Peraturan\s+(?:Pemerintah|Menteri))/gi;

const DELEGATE_PATTERN = /Peraturan\s+(Pemerintah|Menteri)/i;

const INDONESIAN_STOP_WORDS = new Set([
  'dan',
  'atau',
  'yang',
  'dengan',
  'untuk',
  'pada',
  'dalam',
  'dari',
  'ke',
  'di',
  'ini',
  'itu',
  'tersebut',
  'sebagai',
  'oleh',
  'adalah',
  'akan',
  'telah',
  'sudah',
  'dapat',
  'harus',
  'tidak',
  'bisa',
  'serta',
  'maupun',
  'bahwa',
  'karena',
  'maka',
  'jika',
  'apabila',
  'bila',
  'hingga',
  'sampai',
  'tentang',
  'mengenai',
  'menyangkut',
]);

export function detectEnablingClauses(text: string): EnablingClauseMatch[] {
  const matches: EnablingClauseMatch[] = [];

  ENABLING_CLAUSE_PATTERN.lastIndex = 0;

  let match;
  while ((match = ENABLING_CLAUSE_PATTERN.exec(text)) !== null) {
    const [fullMatch, topic, delegateRawText] = match;

    const delegateType = extractDelegateType(delegateRawText);

    matches.push({
      fullMatch,
      topic: topic.trim(),
      delegateRawText,
      delegateType,
      startIndex: match.index,
      endIndex: match.index + fullMatch.length,
    });
  }

  return matches;
}

export function extractDelegateType(delegateRawText: string): DelegateRegulationType {
  const match = delegateRawText.match(DELEGATE_PATTERN);
  if (!match) return 'unknown';

  const key = `Peraturan ${match[1]}`;
  return DELEGATE_TYPE_MAP[key] || 'unknown';
}

export function extractTopicBigrams(topic: string): string[] {
  const words = topic
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 1)
    .filter((word) => !INDONESIAN_STOP_WORDS.has(word));

  if (words.length < 2) {
    return words;
  }

  const bigrams: string[] = [];
  for (let i = 0; i < words.length - 1; i++) {
    bigrams.push(`${words[i]} ${words[i + 1]}`);
  }

  return bigrams;
}

export function extractTopicTrigrams(topic: string): string[] {
  const words = topic
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 1)
    .filter((word) => !INDONESIAN_STOP_WORDS.has(word));

  if (words.length < 3) {
    return [];
  }

  const trigrams: string[] = [];
  for (let i = 0; i < words.length - 2; i++) {
    trigrams.push(`${words[i]} ${words[i + 1]} ${words[i + 2]}`);
  }

  return trigrams;
}

export interface CitingRegulation {
  regulationId: string;
  regulationTitle: string | null;
  regulationType: string;
  regulationNumber: string;
  regulationYear: string;
  isRevoked?: boolean;
  amendmentCategory?: string | null;
}

/**
 * Determines if a regulation is a base regulation that is not revoked.
 * A base regulation has amendmentCategory of 'base' or null (no category assigned).
 */
function isBaseActive(reg: CitingRegulation): boolean {
  const isBase = reg.amendmentCategory === 'base' || reg.amendmentCategory === null || reg.amendmentCategory === undefined;
  return isBase && reg.isRevoked === false;
}

/**
 * Determines if a regulation is not revoked, regardless of category.
 */
function isAnyActive(reg: CitingRegulation): boolean {
  return reg.isRevoked === false;
}

/**
 * Selects the best matching regulation from multiple candidates.
 * Priority:
 * 1. If exactly one base active candidate, return it
 * 2. If multiple base active candidates, return null (ambiguous)
 * 3. Fallback: if exactly one any active candidate, return it
 * 4. If multiple any active candidates, return null (ambiguous)
 * 5. If no active candidates, return null
 */
function selectBestCandidate(candidates: CitingRegulation[]): CitingRegulation | null {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  const baseActiveCandidates = candidates.filter(isBaseActive);
  const anyActiveCandidates = candidates.filter(isAnyActive);

  if (baseActiveCandidates.length === 1) {
    return baseActiveCandidates[0];
  }

  if (baseActiveCandidates.length > 1) {
    if (anyActiveCandidates.length === 1) {
      return anyActiveCandidates[0];
    }
    return null;
  }

  if (anyActiveCandidates.length === 1) {
    return anyActiveCandidates[0];
  }

  return null;
}

export function findMatchingCitingRegulation(
  topic: string,
  delegateType: DelegateRegulationType,
  citingRegulations: CitingRegulation[]
): CitingRegulation | null {
  if (delegateType === 'unknown' || !citingRegulations.length) {
    return null;
  }

  const regulationTypes = REGULATION_TYPE_TO_DELEGATE[delegateType] || [];
  if (regulationTypes.length === 0) return null;

  const trigrams = extractTopicTrigrams(topic);
  const bigrams = extractTopicBigrams(topic);

  if (trigrams.length === 0 && bigrams.length === 0) return null;

  const typeMatchedRegs = citingRegulations.filter((reg) => {
    const regTypeLower = reg.regulationType?.toLowerCase();
    return regulationTypes.some((t) => regTypeLower === t);
  });

  if (typeMatchedRegs.length === 0) return null;
  
  if (typeMatchedRegs.length === 1) {
    return typeMatchedRegs[0];
  }

  if (trigrams.length > 0) {
    const trigramCandidates = typeMatchedRegs.filter((reg) => {
      const titleLower = (reg.regulationTitle || '').toLowerCase();
      return trigrams.some((trigram) => titleLower.includes(trigram));
    });

    if (trigramCandidates.length === 1) {
      return trigramCandidates[0];
    }

    if (trigramCandidates.length > 1) {
      return selectBestCandidate(trigramCandidates);
    }
  }

  if (bigrams.length > 0) {
    const bigramCandidates = typeMatchedRegs.filter((reg) => {
      const titleLower = (reg.regulationTitle || '').toLowerCase();
      return bigrams.some((bigram) => titleLower.includes(bigram));
    });

    if (bigramCandidates.length === 1) {
      return bigramCandidates[0];
    }

    if (bigramCandidates.length > 1) {
      return selectBestCandidate(bigramCandidates);
    }
  }

  return null;
}

export function replaceEnablingClausesWithLinks(
  text: string,
  citingRegulations: CitingRegulation[]
): Array<{ text: string; isLink: boolean; regulationId?: string; linkText?: string }> {
  const matches = detectEnablingClauses(text);

  if (matches.length === 0) {
    return [{ text, isLink: false }];
  }

  const result: Array<{ text: string; isLink: boolean; regulationId?: string; linkText?: string }> =
    [];

  let lastIndex = 0;

  for (const match of matches) {
    if (match.startIndex > lastIndex) {
      result.push({
        text: text.slice(lastIndex, match.startIndex),
        isLink: false,
      });
    }

    const matchedRegulation = findMatchingCitingRegulation(
      match.topic,
      match.delegateType,
      citingRegulations
    );

    if (matchedRegulation) {
      result.push({
        text: match.fullMatch,
        isLink: true,
        regulationId: matchedRegulation.regulationId,
        linkText: match.delegateRawText,
      });
    } else {
      result.push({
        text: match.fullMatch,
        isLink: false,
      });
    }

    lastIndex = match.endIndex;
  }

  if (lastIndex < text.length) {
    result.push({
      text: text.slice(lastIndex),
      isLink: false,
    });
  }

  return result;
}
